import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GlassCard } from "./_shared";
import {
  Inbox, Truck, MessageSquare, CheckCircle2, Images, Sparkles, Landmark,
  MessageSquareQuote, HelpCircle, Megaphone, Activity as ActivityIcon,
  Plus, Edit3, Trash2, Settings as SettingsIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";

function StatCard({ icon: Icon, label, value, gradient, to }) {
  const inner = (
    <div className={`relative overflow-hidden rounded-2xl p-5 border shadow-sm transition-shadow hover:shadow-md ${gradient}`}>
      <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/20 blur-2xl" />
      <div className="relative flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-white/25 grid place-items-center text-white shrink-0"><Icon className="w-5 h-5" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/85 font-semibold">{label}</div>
          <div className="font-display text-2xl font-bold text-white">{value ?? "—"}</div>
        </div>
      </div>
    </div>
  );
  return to ? <Link to={to} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>{inner}</Link> : inner;
}

function QuickAction({ icon: Icon, label, to, color }) {
  return (
    <Link to={to} className={`flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all ${color}`} data-testid={`quick-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="w-10 h-10 rounded-lg bg-[#0057B8]/10 text-[#0057B8] grid place-items-center"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="font-semibold text-slate-900 text-sm">{label}</div>
        <div className="text-xs text-slate-500">Manage</div>
      </div>
    </Link>
  );
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/cms/stats"),
      api.get("/admin/cms/activity", { params: { limit: 10 } }),
      api.get("/admin/orders", { params: { limit: 5 } }),
    ]).then(([s, a, o]) => {
      setStats(s.data);
      setActivity(a.data);
      setRecentOrders(o.data.slice(0, 5));
    }).catch(e => toast.error(formatApiError(e)));
  }, []);

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Overview of your website content and customer leads."
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <StatCard icon={Inbox} label="Total Orders" value={stats?.orders_total} gradient="bg-gradient-to-br from-[#0057B8] to-[#0E7490]" to="/admin/leads" />
        <StatCard icon={Truck} label="New Orders" value={stats?.orders_new} gradient="bg-gradient-to-br from-[#F59E0B] to-[#B45309]" to="/admin/leads" />
        <StatCard icon={CheckCircle2} label="Completed" value={stats?.orders_completed} gradient="bg-gradient-to-br from-emerald-500 to-emerald-700" to="/admin/leads" />
        <StatCard icon={MessageSquare} label="Messages" value={stats?.contacts_total} gradient="bg-gradient-to-br from-sky-500 to-blue-700" to="/admin/leads" />
        <StatCard icon={Images} label="Sliders" value={stats?.slides_total} gradient="bg-gradient-to-br from-fuchsia-500 to-purple-700" to="/admin/sliders" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={Sparkles} label="Services" value={stats?.services_total} gradient="bg-gradient-to-br from-teal-500 to-teal-700" to="/admin/services" />
        <StatCard icon={Landmark} label="Bank Partners" value={stats?.banks_total} gradient="bg-gradient-to-br from-indigo-500 to-indigo-800" to="/admin/banks" />
        <StatCard icon={MessageSquareQuote} label="Testimonials" value={stats?.testimonials_total} gradient="bg-gradient-to-br from-rose-500 to-rose-700" to="/admin/testimonials" />
        <StatCard icon={HelpCircle} label="FAQs" value={stats?.faqs_total} gradient="bg-gradient-to-br from-orange-500 to-orange-700" to="/admin/faqs" />
        <StatCard icon={Megaphone} label="Announcements" value={stats?.announcements_total} gradient="bg-gradient-to-br from-cyan-500 to-cyan-700" to="/admin/announcements" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold text-slate-900">Recent Orders</h2>
              <Link to="/admin/leads" className="text-sm text-[#0057B8] hover:underline">View all →</Link>
            </div>
            {recentOrders.length === 0 ? (
              <div className="text-slate-500 text-sm">No orders yet.</div>
            ) : (
              <div className="space-y-2">
                {recentOrders.map(o => (
                  <div key={o.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <div className="font-medium text-sm">{o.name} <span className="text-slate-400 font-normal">· {o.service_type}</span></div>
                      <div className="text-xs text-slate-500">{o.district} · {o.vehicle_number}</div>
                    </div>
                    <div className="text-xs text-slate-500 whitespace-nowrap">{new Date(o.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>

          <GlassCard className="p-6">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Quick Actions</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <QuickAction icon={Images} label="Manage Sliders" to="/admin/sliders" />
              <QuickAction icon={Sparkles} label="Edit Services" to="/admin/services" />
              <QuickAction icon={MessageSquareQuote} label="Add Testimonial" to="/admin/testimonials" />
              <QuickAction icon={HelpCircle} label="Edit FAQs" to="/admin/faqs" />
              <QuickAction icon={SettingsIcon} label="Site Settings" to="/admin/settings" />
              <QuickAction icon={Megaphone} label="Announcements" to="/admin/announcements" />
            </div>
          </GlassCard>
        </div>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-bold text-slate-900">Recent Activity</h2>
            <Link to="/admin/activity" className="text-sm text-[#0057B8] hover:underline">View all →</Link>
          </div>
          {activity.length === 0 ? (
            <div className="text-slate-500 text-sm">No activity yet.</div>
          ) : (
            <ul className="space-y-3">
              {activity.map((a) => {
                const Icon = a.action === "create" ? Plus : a.action === "delete" ? Trash2 : Edit3;
                return (
                  <li key={a.id} className="flex items-start gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 grid place-items-center shrink-0"><Icon className="w-4 h-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-900 truncate">{a.note}</div>
                      <div className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()} · {a.entity}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </GlassCard>
      </div>
    </>
  );
}
